import { ENVIRONMENT_INITIALIZER, Injectable, Injector, Provider } from '@angular/core';
import { RealtimeEventPayload, RealtimeService } from './realtime.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ExportCreateEvent, RealtimeEventType } from '@flux/core/client';
import { PalantirToastService } from '@flux/design-system';
import { IaraPanelStateService } from '@features/chat/floating/services/iara-panel-state.service';
import { GetPreSignedGQL } from '@generated/graphql';
import { first } from 'rxjs';
import { MySpaceBadgesService } from '@pages/my-space/my-space-badges.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class RealtimeGlobalEventListenerService {
  // ExportId, ToastId
  private exports: Record<string, string> = {};

  // BulkActionId, ToastId
  private bulkActions: Record<string, string> = {};

  public static EXPORT_TYPE_MAP: Record<ExportCreateEvent['data']['type'], string> = {
    cards: 'cards',
    cards_tasks: 'tarefas',
    cards_attachments: 'anexos',
    databases: 'banco de dados',
    reviews: 'aprovações',
    solicitations: 'solicitações',
  };

  public constructor(
    private readonly realtimeService: RealtimeService,
    private readonly injector: Injector,
  ) {
    this.realtimeService.eventReceived$
      .pipe(takeUntilDestroyed())
      .subscribe(this.handleEvent.bind(this));
  }

  public handleEvent(event: RealtimeEventPayload<RealtimeEventType>): void {
    switch (event.op) {
      case RealtimeEventType.ExportCreate:
        this.handleExportCreate(event as RealtimeEventPayload<RealtimeEventType.ExportCreate>);
        break;
      case RealtimeEventType.ExportLogCreate:
        this.handleExportLogCreate(
          event as RealtimeEventPayload<RealtimeEventType.ExportLogCreate>,
        );
        break;
      case RealtimeEventType.ExportUpdate:
        this.handleExportUpdate(event as RealtimeEventPayload<RealtimeEventType.ExportUpdate>);
        break;
      case RealtimeEventType.MySpaceMyCardsCreate:
        this.handleMySpaceTabEvent('my-cards');
        break;
      case RealtimeEventType.MySpaceMyTasksCreate:
        this.handleMySpaceTabEvent('my-tasks');
        break;
      case RealtimeEventType.MySpaceMySolicitationsCreate:
        this.handleMySpaceTabEvent('my-solicitations');
        break;
      case RealtimeEventType.MySpaceMyReviewsCreate:
        this.handleMySpaceTabEvent('my-reviews');
        break;
      case RealtimeEventType.BulkActionCreate:
        this.handleBulkActionCreate(
          event as RealtimeEventPayload<RealtimeEventType.BulkActionCreate>,
        );
        break;
      case RealtimeEventType.BulkActionLogCreate:
        this.handleBulkActionProgress(
          event as RealtimeEventPayload<RealtimeEventType.BulkActionLogCreate>,
        );
        break;
      case RealtimeEventType.BulkActionUpdate:
        this.handleBulkActionUpdate(
          event as RealtimeEventPayload<RealtimeEventType.BulkActionUpdate>,
        );
        break;
      case RealtimeEventType.ChatTurnCompleted:
        this.handleChatTurnCompleted(
          event as RealtimeEventPayload<RealtimeEventType.ChatTurnCompleted>,
        );
        break;
      default:
        break;
    }
  }

  private handleExportCreate(event: RealtimeEventPayload<RealtimeEventType.ExportCreate>): void {
    const palantirToastService = this.injector.get(PalantirToastService);

    const toastId = palantirToastService.showLoading(
      `Sua exportação de ${RealtimeGlobalEventListenerService.EXPORT_TYPE_MAP[event.data.data.type]} está sendo processada, aguarde...`,
      15000,
    );

    this.exports[event.data.data.id] = toastId;
  }

  private handleExportLogCreate(
    event: RealtimeEventPayload<RealtimeEventType.ExportLogCreate>,
  ): void {
    const palantirToastService = this.injector.get(PalantirToastService);
    palantirToastService.toastEmitter$.next({
      id: this.exports[event.data.data.id],
      message: event.data.data.message,
      theme: 'loading',
      timeout: 10000,
    });
  }

  private handleExportUpdate(event: RealtimeEventPayload<RealtimeEventType.ExportUpdate>): void {
    const palantirToastService = this.injector.get(PalantirToastService);
    const toastId = this.exports[event.data.data.id];
    if (toastId) {
      palantirToastService.close(toastId);
      Reflect.deleteProperty(this.exports, event.data.data.id);
    }

    if (event.data.data.status === 'completed') {
      const fileKey = event.data.data.file_key;

      if (!fileKey) {
        palantirToastService.showError(
          `Ocorreu um erro ao processar sua exportação de ${RealtimeGlobalEventListenerService.EXPORT_TYPE_MAP[event.data.data.type]}.`,
          7000,
          undefined,
        );
        return;
      }

      this.downloadFile(fileKey);

      palantirToastService.showSuccess(
        `Sua exportação de ${RealtimeGlobalEventListenerService.EXPORT_TYPE_MAP[event.data.data.type]} foi concluída! Clique para baixar.`,
        0,
        {
          label: 'Baixar arquivo',
          action: () => this.downloadFile(fileKey),
          icon: 'download',
          closeAfterAction: false,
        },
      );

      return;
    }

    palantirToastService.showError(
      `Ocorreu um erro ao processar sua exportação de ${RealtimeGlobalEventListenerService.EXPORT_TYPE_MAP[event.data.data.type]}.`,
      7000,
      undefined,
    );
  }

  private handleMySpaceTabEvent(tab: 'my-reviews' | 'my-tasks' | 'my-cards' | 'my-solicitations') {
    const mySpaceBadgesService = this.injector.get(MySpaceBadgesService);
    mySpaceBadgesService.markTabAsUpdated(tab);
  }

  private handleBulkActionCreate(
    event: RealtimeEventPayload<RealtimeEventType.BulkActionCreate>,
  ): void {
    const palantirToastService = this.injector.get(PalantirToastService);
    const id = palantirToastService.showLoading(
      `Ação em massa está sendo processada, aguarde...`,
      30_000,
    );
    this.bulkActions[event.data.data.id] = id;
  }

  private handleBulkActionProgress(
    event: RealtimeEventPayload<RealtimeEventType.BulkActionLogCreate>,
  ): void {
    const toastId = this.bulkActions[event.data.data.id];
    if (!toastId) return;

    const palantirToastService = this.injector.get(PalantirToastService);

    const { message, status } = event.data.data;

    const isCompletedOrFailed = status === 'completed' || status === 'failed';

    palantirToastService.toastEmitter$.next({
      id: toastId,
      message: message,
      theme: status === 'completed' ? 'success' : status === 'failed' ? 'error' : 'loading',
      timeout: isCompletedOrFailed ? 5_000 : 30_000,
    });
  }

  private handleBulkActionUpdate(
    event: RealtimeEventPayload<RealtimeEventType.BulkActionUpdate>,
  ): void {
    const status = event.data.data.status;
    const palantirToastService = this.injector.get(PalantirToastService);

    if (status === 'in_progress' && !this.bulkActions[event.data.data.id]) {
      const id = palantirToastService.showLoading(
        `Ação em massa está sendo processada, aguarde...`,
        30_000,
      );
      this.bulkActions[event.data.data.id] = id;
      return;
    }

    if (status === 'failed') {
      palantirToastService.toastEmitter$.next({
        id: this.bulkActions[event.data.data.id],
        message: `Ação em massa falhou ao ser processada.`,
        theme: 'error',
        timeout: 5_000,
      });
      Reflect.deleteProperty(this.bulkActions, event.data.data.id);
      return;
    }

    const { failure_count, success_count, output_file_key } = event.data.data;
    const successMessage = `Ação em massa processada com sucesso, ${success_count} itens processados com sucesso e ${failure_count} falhas.`;

    if (output_file_key) {
      const toastId = this.bulkActions[event.data.data.id];
      if (toastId) {
        palantirToastService.close(toastId);
        Reflect.deleteProperty(this.bulkActions, event.data.data.id);
      }

      this.downloadFile(output_file_key);

      palantirToastService.showSuccess(successMessage, 0, {
        label: 'Baixar arquivo',
        action: () => this.downloadFile(output_file_key),
        icon: 'download',
        closeAfterAction: false,
      });

      return;
    }

    palantirToastService.toastEmitter$.next({
      id: this.bulkActions[event.data.data.id],
      message: successMessage,
      theme: 'success',
      timeout: 5_000,
    });
    Reflect.deleteProperty(this.bulkActions, event.data.data.id);
  }

  /**
   * Turno do chat da Iara terminou. Toast global com CTA — exceto quando a conversa
   * já está na frente do usuário (lá o sinal chega pela subscription GraphQL).
   *
   * "Na frente do usuário" tem DUAS formas desde o painel flutuante: a página
   * `/chat/:sessionId` e o painel aberto em qualquer rota. Cobrir só a primeira
   * fazia o aviso disparar justamente para quem estava vendo a resposta chegar.
   *
   * O painel é a fonte de verdade do que está visível, e não a preferência salva de
   * aberto/fechado: em `/chat` o painel não é montado, mesmo com a preferência
   * dizendo "aberto".
   */
  private handleChatTurnCompleted(
    event: RealtimeEventPayload<RealtimeEventType.ChatTurnCompleted>,
  ): void {
    const payload = event.data.data as { session_id: string; outcome: 'completed' | 'failed' };
    const { session_id: sessionId, outcome } = payload;
    const router = this.injector.get(Router);
    if (router.url.startsWith(`/chat/${sessionId}`)) return;
    if (this.injector.get(IaraPanelStateService).isSessionVisible(sessionId)) return;

    const toast = this.injector.get(PalantirToastService);
    const action = {
      label: 'Ver resposta',
      action: () => void router.navigate(['/chat', sessionId]),
      icon: 'chevron-right-outline' as const,
      closeAfterAction: true,
    };

    if (outcome === 'completed') {
      toast.showSuccess('A Iara terminou a análise.', 0, action);
      return;
    }

    toast.showError('A Iara não conseguiu concluir a análise.', 0, action);
  }

  private downloadFile(key: string): void {
    const getPresignedUrl = this.injector.get(GetPreSignedGQL);
    const palantirToastService = this.injector.get(PalantirToastService);

    getPresignedUrl
      .fetch({ key })
      .pipe(first())
      .subscribe((result) => {
        if (result.data?.getPreSigned) {
          window.open(result.data.getPreSigned, '_blank');
        } else {
          palantirToastService.showError('Não foi possível obter o link para download do arquivo.');
        }
      });
  }

  public static provider(): Provider {
    return {
      provide: ENVIRONMENT_INITIALIZER,
      useFactory: (realtimeService: RealtimeService, injector: Injector) => () =>
        new RealtimeGlobalEventListenerService(realtimeService, injector),
      deps: [RealtimeService, Injector],
      multi: true,
    };
  }
}
